import { create } from 'zustand';
import { adminArticleApi } from '@api/api-config.ts';
import { ArticleDetailResponse, ArticleNotificationResponse, CreateArticleRequest, UpdateArticleRequest } from '@api/generated/portal-be-admin';

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
}

interface AdminArticleDetailStoreState {
  // Detail
  loading: boolean;
  error: string | null;
  article: ArticleDetailResponse | null;

  // Notifikace
  notifications: ArticleNotificationResponse[];
  notificationsLoading: boolean;
  notificationsError: string | null;
  notificationsPagination: PaginationInfo | null;

  // Akce
  loadArticleDetail: (articleId: number) => Promise<void>;
  loadNotifications: (articleId: number, params: { page?: number; size?: number; q?: string; isRead?: boolean }) => Promise<void>;
  createArticle: (data: CreateArticleRequest) => Promise<number>;
  updateArticle: (articleId: number, data: UpdateArticleRequest) => Promise<void>;
  deleteArticle: (articleId: number) => Promise<void>;
  reset: () => void;
}

export const useAdminArticleDetailStore = create<AdminArticleDetailStoreState>(set => ({
  loading: false,
  error: null,
  article: null,

  notifications: [],
  notificationsLoading: false,
  notificationsError: null,
  notificationsPagination: null,

  loadArticleDetail: async (articleId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await adminArticleApi.getArticleDetail({ articleId });
      set({
        loading: false,
        error: null,
        article: response.data,
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba při načítání detailu článku',
        article: null,
      });
    }
  },

  loadNotifications: async (articleId: number, params) => {
    try {
      set({ notificationsLoading: true, notificationsError: null });
      const response = await adminArticleApi.getArticleNotifications({
        articleId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
        isRead: params.isRead,
      });
      const result = response.data;
      set({
        notificationsLoading: false,
        notificationsError: null,
        notifications: result.items || [],
        notificationsPagination: {
          page: result.page,
          size: result.size,
          total: result.total,
          totalPages: result.totalPages,
          hasPrev: result.hasPrev,
          hasNext: result.hasNext,
        },
      });
    } catch (error) {
      set({
        notificationsLoading: false,
        notificationsError: error instanceof Error ? error.message : 'Chyba při načítání notifikací',
        notifications: [],
        notificationsPagination: null,
      });
    }
  },

  createArticle: async (data: CreateArticleRequest) => {
    const response = await adminArticleApi.createArticle({ createArticleRequest: data });
    // response.data je { [key: string]: number } - pravděpodobně { id: number }
    const result = response.data;
    return result.id || Object.values(result)[0];
  },

  updateArticle: async (articleId: number, data: UpdateArticleRequest) => {
    await adminArticleApi.updateArticle({ articleId, updateArticleRequest: data });
  },

  deleteArticle: async (articleId: number) => {
    await adminArticleApi.deleteArticle({ articleId });
  },

  reset: () => {
    set({
      loading: false,
      error: null,
      article: null,
      notifications: [],
      notificationsLoading: false,
      notificationsError: null,
      notificationsPagination: null,
    });
  },
}));
